import { useState, FormEvent, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { loginWithUsername, loginWithProvider, getMe } from './core/auth';
import { GoogleOAuthProvider } from '@react-oauth/google';
import GoogleLoginButton from './components/auth/google-login-btn';
import { LoginProps } from './type';
import FacebookLoginButton from './components/auth/facebook-login-btn';
import AppleLoginButton from './components/auth/apple-login-btn';
import ZaloLoginButton from './components/auth/zalo-login-btn';
import SignupForm from './components/auth/signup-form';
import ForgotPasswordForm from './components/auth/forgot-password';
import { showError } from './core/utils';
import QuickLoginButton, { QuickLoginButtonRef } from './components/auth/quick-login-btn';
import { HiOutlineEye, HiOutlineEyeSlash } from 'react-icons/hi2';

export interface AuthRef {
  triggerQuickLogin: () => Promise<void>;
}

type AuthViewMode = 'login' | 'signup' | 'forgot';

const AuthLogin = forwardRef<AuthRef, LoginProps>(({
  product,
  apiEndpoint,
  onLoginSuccess = () => { },
  onLoginError = () => { },
  privacyHref = 'https://nap.joyplay.vn/chinh-sach-bao-mat',
  onClickClose = () => { }
}, ref) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [form, setForm] = useState<AuthViewMode>("signup");
  const [loginPwdVisible, setLoginPwdVisible] = useState(false);
  const quickLoginRef = useRef<QuickLoginButtonRef>(null);

  useImperativeHandle(ref, () => ({
    triggerQuickLogin: async () => {
      if (quickLoginRef.current) {
        await quickLoginRef.current.triggerLogin();
      }
    }
  }));

  const title = useMemo(() => {
    if (form === 'signup') return 'Đăng ký';
    if (form === 'forgot') return 'Quên mật khẩu';
    return 'Đăng nhập';
  }, [form]);

  const { showGoogle, showFacebook, showApple, showZalo, showQuickLogin, productId, googleClientId, facebookAppId, bundleId, oauthCallbackURI } = useMemo(() => {
    const isWebEvent = product.mode === 'web-event';
    return {
      showGoogle: isWebEvent ? !!product.showGoogleWebEvent : !!product.showGoogle,
      showFacebook: isWebEvent ? !!product.showFacebookWebEvent : !!product.showFacebook,
      showApple: isWebEvent ? !!product.showAppleWebEvent : !!product.showApple,
      showZalo: isWebEvent ? !!product.showZaloWebEvent : !!product.showZalo,
      showQuickLogin: isWebEvent ? !!product.showQuickLoginWebEvent : !!product.showQuickLogin,
      productId: product?.code || product?.id,
      // googleClientId: product.googleClientId as string | undefined || '800298836643-3g206n5otl752t1878pieb329jkka28u.apps.googleusercontent.com',
      // facebookAppId: (product.facebookAppId as string | undefined) || '1537939000186463',
      googleClientId: '800298836643-3g206n5otl752t1878pieb329jkka28u.apps.googleusercontent.com',
      facebookAppId: '1537939000186463',
      bundleId: product.bundleId as string | undefined,
      oauthCallbackURI: product.oauthCallbackURI as string | undefined
    };
  }, [product]);

  const hasSocials = showGoogle || showFacebook || showApple || showZalo || showQuickLogin;

  return (
    <div className='modal_wrapper small' id="auth-modal">
      {product.mode !== 'web-event' && (
        <button type="button" className='close-btn' onClick={() => { onClickClose(); }} aria-label="Đóng">
          ×
        </button>
      )}
      <h1 className='title'>{title}</h1>

      {form === 'login' && (
        <>
          <form
            onSubmit={async (event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              const username = (formData.get('username') as string || '').trim();
              const password = formData.get('password') as string;
              const confirmCheckbox = formData.get("confirmCheckbox");
              if (!confirmCheckbox) {
                showError("Bạn phải đồng ý với chính sách bảo mật thông tin");
                return;
              }
              try {
                setLoading(true);
                const data = await loginWithUsername({
                  username,
                  password,
                  productId
                }, apiEndpoint);
                onLoginSuccess({ ...data, loginMethod: 'email' });
              } catch (e: any) {
                showError(e?.response?.data || e);
                onLoginError(e?.response?.data || e);
              } finally {
                setLoading(false);
              }
            }}
            className="auth_form"
          >
            <div className="form_field_group">
              <label className="field_label" htmlFor="login-username">Email</label>
              <div className="form_item">
                <input
                  type="email"
                  id="login-username"
                  name="username"
                  placeholder="Nhập email của bạn"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="form_field_group">
              <label className="field_label" htmlFor="login-password">Mật khẩu</label>
              <div className="form_item">
                <input
                  id="login-password"
                  name="password"
                  type={loginPwdVisible ? 'text' : 'password'}
                  placeholder="Nhập mật khẩu"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="password_eye_toggle"
                  onClick={() => setLoginPwdVisible(v => !v)}
                  aria-label={loginPwdVisible ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                  tabIndex={-1}
                >
                  {loginPwdVisible ? <HiOutlineEye /> : <HiOutlineEyeSlash />}
                </button>
              </div>
            </div>

            <div className="forgot_pass_row">
              <button type="button" onClick={() => setForm("forgot")}>Quên mật khẩu?</button>
            </div>

            <div className="form_item checkbox">
              <label htmlFor="login-confirmCheckbox">
                <input type="checkbox" id="login-confirmCheckbox" defaultChecked name="confirmCheckbox" />
                <span>
                  Tôi đồng ý với
                  <a href={privacyHref} target="_blank" rel="noopener noreferrer"> Chính sách bảo mật</a>
                </span>
              </label>
            </div>

            <button type="submit" className="primary_btn" disabled={loading}>Đăng nhập</button>
          </form>

          {hasSocials && (
            <>
              <div className="divider"><span>Hoặc tiếp tục với</span></div>
              <div className='social_btns'>
                {showGoogle && googleClientId && (
                  <GoogleOAuthProvider clientId={googleClientId}>
                    <GoogleLoginButton
                      apiEndpoint={apiEndpoint}
                      onSuccess={(data) => loginWithProvider("google", data, apiEndpoint, productId)
                        .then((res) => onLoginSuccess({ ...res, loginMethod: 'google', isNewUser: !!res.isNewUser }))
                        .catch(onLoginError.bind(this))}
                      onError={onLoginError.bind(this)} />
                  </GoogleOAuthProvider>
                )}
                {showFacebook && facebookAppId && (
                  <FacebookLoginButton
                    apiEndpoint={apiEndpoint}
                    facebookAppId={facebookAppId}
                    onSuccess={(data) => loginWithProvider("facebook", data, apiEndpoint, productId)
                      .then((res) => onLoginSuccess({ ...res, loginMethod: 'facebook', isNewUser: !!res.isNewUser }))
                      .catch(onLoginError.bind(this))}
                    onError={onLoginError.bind(this)} />
                )}
                {showApple && bundleId && (
                  <AppleLoginButton
                    redirectURI={oauthCallbackURI}
                    apiEndpoint={apiEndpoint}
                    appleClientId='com.joyplay.signin'
                    onSuccess={(data) => loginWithProvider("apple", data, apiEndpoint, productId)
                      .then((res) => onLoginSuccess({ ...res, loginMethod: 'apple', isNewUser: !!res.isNewUser }))
                      .catch(onLoginError.bind(this))}
                    onError={onLoginError.bind(this)} />
                )}
                {showZalo && (
                  <ZaloLoginButton
                    apiEndpoint={apiEndpoint}
                    onSuccess={(data) => loginWithProvider("zalo", data, apiEndpoint, productId)
                      .then((res) => onLoginSuccess({ ...res, loginMethod: 'zalo', isNewUser: !!res.isNewUser }))
                      .catch(onLoginError.bind(this))}
                    onError={onLoginError.bind(this)} />
                )}
                {showQuickLogin && (
                  <QuickLoginButton
                    ref={quickLoginRef}
                    apiEndpoint={apiEndpoint}
                    productId={productId}
                    onSuccess={(data) => onLoginSuccess({ ...data, loginMethod: 'guest', isNewUser: !!data.isNewUser })}
                    onError={onLoginError.bind(this)} />
                )}
              </div>
            </>
          )}

          <div className='auth_btns'>
            <button type="button" onClick={() => setForm("signup")}>
              Chưa có tài khoản? <span className="switch_link_highlight">Đăng ký</span>
            </button>
          </div>
        </>
      )}

      {form === 'signup' && (
        <>
          <SignupForm
            product={product}
            apiEndpoint={apiEndpoint}
            privacyHref={privacyHref}
            onSuccess={async (payload: any) => {
              try {
                const user = await getMe(payload.accessToken, apiEndpoint);
                onLoginSuccess({ ...payload, user, loginMethod: 'email', isNewUser: true });
              } catch (e: any) {
                onLoginError(e?.response?.data || e);
              }
            }}
            onError={onLoginError}
          />

          {hasSocials && (
            <>
              <div className="divider"><span>Hoặc tiếp tục với</span></div>
              <div className='social_btns'>
                {showGoogle && googleClientId && (
                  <GoogleOAuthProvider clientId={googleClientId}>
                    <GoogleLoginButton
                      apiEndpoint={apiEndpoint}
                      onSuccess={(data) => loginWithProvider("google", data, apiEndpoint, productId)
                        .then((res) => onLoginSuccess({ ...res, loginMethod: 'google', isNewUser: !!res.isNewUser }))
                        .catch(onLoginError.bind(this))}
                      onError={onLoginError.bind(this)} />
                  </GoogleOAuthProvider>
                )}
                {showFacebook && facebookAppId && (
                  <FacebookLoginButton
                    apiEndpoint={apiEndpoint}
                    facebookAppId={facebookAppId}
                    onSuccess={(data) => loginWithProvider("facebook", data, apiEndpoint, productId)
                      .then((res) => onLoginSuccess({ ...res, loginMethod: 'facebook', isNewUser: !!res.isNewUser }))
                      .catch(onLoginError.bind(this))}
                    onError={onLoginError.bind(this)} />
                )}
                {showApple && bundleId && (
                  <AppleLoginButton
                    redirectURI={oauthCallbackURI}
                    apiEndpoint={apiEndpoint}
                    appleClientId='com.joyplay.signin'
                    onSuccess={(data) => loginWithProvider("apple", data, apiEndpoint, productId)
                      .then((res) => onLoginSuccess({ ...res, loginMethod: 'apple', isNewUser: !!res.isNewUser }))
                      .catch(onLoginError.bind(this))}
                    onError={onLoginError.bind(this)} />
                )}
                {showZalo && (
                  <ZaloLoginButton
                    apiEndpoint={apiEndpoint}
                    onSuccess={(data) => loginWithProvider("zalo", data, apiEndpoint, productId)
                      .then((res) => onLoginSuccess({ ...res, loginMethod: 'zalo', isNewUser: !!res.isNewUser }))
                      .catch(onLoginError.bind(this))}
                    onError={onLoginError.bind(this)} />
                )}
                {showQuickLogin && (
                  <QuickLoginButton
                    ref={quickLoginRef}
                    apiEndpoint={apiEndpoint}
                    productId={productId}
                    onSuccess={(data) => onLoginSuccess({ ...data, loginMethod: 'guest', isNewUser: !!data.isNewUser })}
                    onError={onLoginError.bind(this)} />
                )}
              </div>
            </>
          )}

          <div className='auth_btns'>
            <button type="button" onClick={() => setForm("login")}>
              Đã có tài khoản? <span className="switch_link_highlight">Đăng nhập</span>
            </button>
          </div>
        </>
      )}

      {form === 'forgot' && (
        <>
          <ForgotPasswordForm apiEndpoint={apiEndpoint} />
          <div className='auth_btns' style={{ gap: 16, flexWrap: 'wrap' }}>
            <button type="button" className='left-btn' onClick={() => setForm("login")}>Đăng nhập</button>
            <button type="button" className='gray' onClick={() => setForm("signup")}>Đăng ký</button>
          </div>
        </>
      )}
    </div>
  );
});

AuthLogin.displayName = 'AuthLogin';

export default AuthLogin;
